import PropTypes from "prop-types";

/**
 * PropTypes for CreateCredentialModal component.
 *
 * @property {boolean} isOpen - Whether the modal is currently open
 * @property {Function} onClose - Callback when modal should close
 * @property {Function} onSuccess - Callback when credentials are successfully created
 * @property {Function} addToast - Function to display toast notifications
 */
export const createCredentialModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  addToast: PropTypes.func.isRequired,
};

/**
 * PropTypes for DeleteCredentialModal component.
 *
 * @property {boolean} isOpen - Whether the modal is currently open
 * @property {Function} onClose - Callback when modal should close
 * @property {Object} [credential] - Credential object to delete
 * @property {Function} onConfirm - Callback to execute delete operation
 * @property {Function} addToast - Function to display toast notifications
 */
export const deleteCredentialModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  credential: PropTypes.shape({
    documentId: PropTypes.string.isRequired,
    name: PropTypes.string,
    provider_type: PropTypes.string.isRequired,
    provider_mode: PropTypes.string.isRequired,
    is_active: PropTypes.bool.isRequired,
  }),
  onConfirm: PropTypes.func.isRequired,
  addToast: PropTypes.func.isRequired,
};

/**
 * PropTypes for DeactivateCredentialModal component.
 *
 * @property {boolean} isOpen - Whether the modal is currently open
 * @property {Function} onClose - Callback when modal should close
 * @property {Object} [credential] - Credential object to deactivate
 * @property {Function} onConfirm - Callback to execute deactivate operation
 * @property {Function} addToast - Function to display toast notifications
 */
export const deactivateCredentialModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  credential: PropTypes.shape({
    documentId: PropTypes.string.isRequired,
    name: PropTypes.string,
    provider_mode: PropTypes.string.isRequired,
  }),
  onConfirm: PropTypes.func.isRequired,
  addToast: PropTypes.func.isRequired,
};

/**
 * PropTypes for EditCredentialModal component.
 *
 * @property {boolean} isOpen - Whether the modal is currently open
 * @property {Function} onClose - Callback when modal should close
 * @property {Object} [credential] - Credential object to edit
 * @property {Function} onSave - Callback to update credentials
 */
export const editCredentialModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  credential: PropTypes.shape({
    documentId: PropTypes.string.isRequired,
    name: PropTypes.string,
    provider_type: PropTypes.string.isRequired,
    provider_mode: PropTypes.string.isRequired,
    webhook_secret: PropTypes.string,
  }),
  onSave: PropTypes.func.isRequired,
};

/**
 * PropTypes for Payment Credentials SidebarSection component.
 *
 * @property {Object} [credential] - Currently selected credential object
 * @property {Object} filters - Current filter state with environment and status
 * @property {Function} onFilterChange - Callback when filter values change
 * @property {Array} environmentOptions - Available environment filter options
 * @property {Array} statusOptions - Available status filter options
 * @property {Function} onEditClick - Callback to open edit modal
 * @property {Function} onActivate - Callback to activate credential
 * @property {Function} onValidate - Callback to validate stored credentials
 * @property {Function} onDeactivateClick - Callback to open deactivate confirmation
 * @property {Function} onDeleteClick - Callback to open delete confirmation
 * @property {boolean} [isValidating] - Validation loading state
 */
export const paymentCredentialsSidebarSectionPropTypes = {
  credential: PropTypes.shape({
    documentId: PropTypes.string.isRequired,
    name: PropTypes.string,
    provider_type: PropTypes.string.isRequired,
    provider_mode: PropTypes.string.isRequired,
    is_active: PropTypes.bool.isRequired,
    publishable_key: PropTypes.string,
    webhook_secret: PropTypes.string,
    createdAt: PropTypes.string,
    last_tested_at: PropTypes.string,
  }),
  filters: PropTypes.shape({
    environment: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  environmentOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onEditClick: PropTypes.func.isRequired,
  onActivate: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  onDeactivateClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  isValidating: PropTypes.bool,
};
